/**
 * Game log formatting utilities.
 *
 * Pure functions — no React imports, no side effects. Every function takes
 * a GameEvent (or a bundle of them) and resolver functions and returns
 * a plain-English string suitable for display in the log screen.
 *
 * Dice are rendered as `[n]` tokens, which renderLogText draws as numeral tiles.
 */

import type { GameEvent, MapDef } from '@warcarp/api-types'
import { teamLabel } from '@warcarp/api-types'

// ─── Time-ago formatter ──────────────────────────────────────────────────────

/**
 * Render a relative time-ago label for a millisecond epoch timestamp.
 *
 *   < 60 s        → "just now"
 *   < 60 min      → "Nm"
 *   < 24 h        → "Nh"
 *   < 7 days      → "Nd"
 *   otherwise     → an absolute "MMM D" date
 *
 * Intentionally terse — these labels render in narrow log rows next
 * to the action description; long phrases would crowd them out.
 */
export function formatTimeAgo(ts: number, now: number = Date.now()): string {
  const delta = Math.max(0, now - ts)
  const sec = Math.floor(delta / 1000)
  if (sec < 60) return 'just now'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}m`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h`
  const day = Math.floor(hr / 24)
  if (day < 7) return `${day}d`
  // Older than a week — fall back to a short absolute date.
  const d = new Date(ts)
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${MONTHS[d.getMonth()]} ${d.getDate()}`
}

// ─── Dice ─────────────────────────────────────────────────────────────────────

/**
 * Wrap a face value as a die TOKEN: `[9]`.
 *
 * Every die renders as a numeral tile, drawn by `renderLogText`. The Unicode pip
 * faces (⚀–⚅) were used for one to six and are gone: they stop at six, so a
 * custom-dice border — a Capture the Flag tower rolling d9 — had nothing to be
 * drawn as and fell back to a bare digit leaning against two pip glyphs. Adding
 * tiles for the big faces then left a roll reading in two visual languages at
 * once, with two sets of font metrics to keep aligned. One language for every face
 * is both simpler and what a die actually needs to say: which number came up.
 *
 * Deliberately square brackets rather than a private-use sentinel: this string also
 * reaches places that never see the React renderer (push notification bodies,
 * tests, any future plain-text surface), and `[9]` is the right answer there too.
 */
export function dieToken(face: number): string {
  return `[${face}]`
}

/** Render one side's die faces as tokens. */
export function renderDice(faces: number[]): string {
  return faces.map(dieToken).join('')
}

// ─── Resolvers ────────────────────────────────────────────────────────────────

/** Resolve a PlayerId (slot string) to a display name. */
export type PlayerResolver = (playerId: string) => string

/** Build a territory-name resolver from a MapDef (returns the ID if name not found). */
export function makeTerritoryResolver(map: MapDef | null | undefined): (id: string) => string {
  if (!map) return (id) => id
  const byId = new Map(map.territories.map((t) => [t.id, t.name]))
  return (id) => byId.get(id) ?? id
}

// ─── Event formatters ─────────────────────────────────────────────────────────

/**
 * Format a single GameEvent into a plain-English primary line.
 * Returns null for event kinds that are intentionally suppressed.
 *
 * Phase-advance events are suppressed: they fire on every phase transition
 * and produce a row for every action, making the log extremely noisy without
 * adding meaningful information. The turn-start event already marks when a new
 * turn begins; the surrounding action events convey the current phase implicitly.
 */
export function formatEventLine(
  event: GameEvent,
  resolve: PlayerResolver,
  territoryName: (id: string) => string,
): string | null {
  // Fog of war: a territory the viewer couldn't see when it happened arrives
  // as '' and reads as "unknown".
  const placeName = (id: string) => (id === '' ? 'unknown' : territoryName(id))
  switch (event.kind) {
    case 'turn-start': {
      const actor = resolve(event.player)
      // `armies` is optional: turn-start events logged before the field existed
      // (and the claim-phase opener, which has no reinforcement yet) omit it, so
      // fall through to the bare heading rather than printing "(undefined …)".
      const received =
        typeof event.armies === 'number'
          ? ` (${event.armies} troop${event.armies === 1 ? '' : 's'} received)`
          : ''
      return `Turn ${event.turnNumber} — ${actor} begins${received}`
    }

    case 'phase-advance':
      return null

    case 'claim': {
      const actor = resolve(event.player)
      const tName = placeName(event.territoryId)
      return `${actor} claims ${tName} (${event.armies} arm${event.armies === 1 ? 'y' : 'ies'})`
    }

    case 'placement': {
      const actor = resolve(event.player)
      if (event.placements.length === 0) return `${actor} places armies`
      const total = event.placements.reduce((sum, p) => sum + p.armies, 0)
      if (event.placements.length === 1) {
        const only = event.placements[0]!
        const tName = territoryName(only.territoryId)
        return `${actor} places ${only.armies} arm${only.armies === 1 ? 'y' : 'ies'} on ${tName}`
      }
      const breakdown = event.placements
        .map((p) => `${territoryName(p.territoryId)} +${p.armies}`)
        .join(', ')
      return `${actor} places ${total} arm${total === 1 ? 'y' : 'ies'} (${breakdown})`
    }

    case 'attack': {
      const actor = resolve(event.attacker)
      const defender = event.defender === '' ? 'neutral' : resolve(event.defender)
      const fromName = placeName(event.fromTerritoryId)
      const toName = placeName(event.toTerritoryId)
      // Fog of war: one end out of view. No dice, and the unseen end's
      // player (heavy fog) and losses read "unknown".
      if (event.unseen || event.fromTerritoryId === '' || event.toTerritoryId === '') {
        const seenFrom = event.unseen ? event.unseen === 'to' : event.toTerritoryId === ''
        const who = (slot: string) => (slot === '' ? 'unknown' : resolve(slot))
        return (
          `${seenFrom ? actor : who(event.attacker)} attacks ${seenFrom ? who(event.defender) : defender}${event.isFriendlyFire ? ' (teammate)' : ''} (${fromName} → ${toName})\n` +
          `(${seenFrom ? `${event.attackerLost} lost` : 'unknown'}, ${seenFrom ? 'unknown' : `${event.defenderLost} lost`})`
        )
      }
      const atkDice = renderDice(event.roll.attacker)
      const defDice = renderDice(event.roll.defender)
      // Team games: the engine flags an attack on a teammate.
      const friendly = event.isFriendlyFire ? ' (teammate)' : ''
      // Dice always start on their own line (newline after the "(from → to)")
      // so the roll reads as its own row instead of wrapping unpredictably
      // mid-line in the game log.
      return (
        `${actor} attacks ${defender}${friendly} (${fromName} → ${toName})\n` +
        `${atkDice} vs ${defDice} ` +
        `(${event.attackerLost} lost, ${event.defenderLost} lost)`
      )
    }

    case 'conquest': {
      const actor = resolve(event.attacker)
      const prev = event.defender === '' ? 'neutral' : resolve(event.defender)
      const tName = placeName(event.territoryId)
      const n = event.armiesMoved
      // Fog of war hides the troops of conquests you couldn't see.
      if (n === undefined) return `${actor} conquers ${tName} from ${prev}`
      return `${actor} conquers ${tName} from ${prev} (moved ${n} arm${n === 1 ? 'y' : 'ies'})`
    }

    case 'post-conquest-transfer': {
      const actor = resolve(event.player)
      const fromName = placeName(event.fromTerritoryId)
      const toName = placeName(event.toTerritoryId)
      return `${actor} moves ${event.armies} from ${fromName} to ${toName}`
    }

    case 'elimination': {
      const actor = resolve(event.eliminator)
      const victim = resolve(event.eliminated)
      const cards = event.cardsInherited
      // Fog of war hides the inherited cards and bonus from other sides.
      if (cards === undefined || event.bonusGained === undefined) return `${victim} is eliminated by ${actor}`
      const cardStr = cards === 1 ? '1 card' : `${cards} cards`
      return `${victim} is eliminated by ${actor} (inherited ${cardStr}, +${event.bonusGained} elimination bonus)`
    }

    case 'hidden':
      // Fog of war: one row per hidden move, so the log shows when each
      // happened (and each opens the snapshot at that point).
      return `${resolve(event.player)} made a move in the fog`

    case 'card-draw': {
      const actor = resolve(event.player)
      return `${actor} draws a card`
    }

    case 'card-turn-in': {
      const actor = resolve(event.player)
      return `${actor} turns in cards (+${event.bonus} armies)`
    }

    case 'fortify': {
      const actor = resolve(event.player)
      const fromName = placeName(event.fromTerritoryId)
      const toName = placeName(event.toTerritoryId)
      const n = event.armies
      // Fog of war: one end out of sight, so say exactly what was seen.
      if (event.fromTerritoryId === '' || event.toTerritoryId === '') {
        return `${actor} fortifies ${n} troop${n === 1 ? '' : 's'} from ${fromName} to ${toName}`
      }
      return `${actor} fortifies ${fromName} → ${toName} (${n} arm${n === 1 ? 'y' : 'ies'})`
    }

    case 'resign': {
      const actor = resolve(event.player)
      return `${actor} resigned`
    }

    // The seat now resolves to the bot's name, so use the names captured at takeover.
    case 'ai-takeover':
      return event.bootedByName
        ? `${event.bootedByName} booted ${event.playerName}, who was replaced by ${event.botName}`
        : event.reason === 'abandoned'
        ? `${event.playerName} abandoned the game — ${event.botName} will now take over`
        : `${event.playerName}'s time expired — ${event.botName} will now take over`

    case 'game-end': {
      // Team games name the winning team; every member wins.
      if (event.winnerTeam) {
        const team = teamLabel(event.winnerTeam)
        if (event.reason === 'resign') return `${team} wins — last team standing after resignations`
        if (event.reason === 'stalled') return `${team} wins — ahead at the turn limit`
        return `${team} wins!`
      }
      if (event.winner) {
        const winner = resolve(event.winner)
        if (event.reason === 'resign') return `${winner} wins — last standing after resignations`
        if (event.reason === 'stalled') return `${winner} wins — most territories at turn limit`
        return `${winner} wins! (last standing)`
      }
      return 'Game stalled — no winner'
    }

    // Team games: the exit of `lastPlayer` left the team with nobody in play.
    case 'team-eliminated':
      return `${teamLabel(event.team)} has been eliminated (${resolve(event.lastPlayer)} was the last one standing)`
  }
}

// ─── Event grouping ───────────────────────────────────────────────────────────

/** Threshold in milliseconds within which two events are considered the same action. */
const BUNDLE_THRESHOLD_MS = 5

/**
 * A rendered event row: a primary line plus optional sub-lines from bundled events.
 */
export interface EventRow {
  /** Unique key derived from the primary event index + kind. */
  key: string
  /** Primary display line. */
  primary: string
  /** Secondary lines (conquest, elimination, etc.) that share the same timestamp. */
  sub: string[]
  /**
   * All event kinds represented by this row — the primary event's kind
   * plus every bundled sub-event's kind (conquest, elimination,
   * post-conquest-transfer). The log-page kind filter checks this set,
   * so filtering by "conquest" matches the attack rows that produced a
   * conquest (conquest events are never standalone rows — they're
   * always bundled into the attack that caused them).
   */
  kinds: GameEvent['kind'][]
  /** Turn number from the primary event. */
  turn: number
  /** Epoch-ms timestamp of the primary event. */
  ts: number
  /** Whether this is a turn-start marker. */
  isTurnStart: boolean
  /** Whether this is the game-end event. */
  isGameEnd: boolean
  /** PlayerId (slot string) of the primary actor — used for colour dots. */
  playerId: string
  /**
   * PlayerId (slot string) of the *other* player in a two-sided row —
   * the defender on an attack/conquest, or the eliminated player on a
   * killing blow. Empty string when the defender is neutral. Undefined
   * for single-actor rows (placement, fortify, claim, etc.). Used to
   * render a second player-colour dot so combat rows are scannable at
   * a glance.
   */
  defenderId?: string
  /**
   * All player slots associated with this row's events — drives the
   * by-player filter. For an attack/conquest bundle this includes BOTH
   * the attacker and the defender (and the eliminated player on a
   * killing blow), so filtering by either side surfaces the row. For
   * single-actor events it's just `[playerId]`.
   */
  relatedPlayerIds: string[]
  /**
   * Index of the FIRST event represented by this row. Together with
   * `eventIndex`, this lets deep links to a raw event find the rendered row
   * that contains it (for example attack + conquest + transfer bundles).
   */
  startEventIndex?: number
  /**
   * Index of the LAST event in the underlying events array that this
   * row represents. For bundled rows (attack + conquest + elimination
   * + transfer) this is the index of the last bundled event so a
   * time-machine view set to this index shows the post-conquest map.
   * For single-event rows it's the event's own index.
   */
  eventIndex: number
}

/**
 * Helper: return the "actor" PlayerId for events that have one.
 * For attack/conquest/elimination the actor is the aggressor.
 */
function eventPlayerId(evt: GameEvent): string {
  switch (evt.kind) {
    case 'attack':
      return evt.attacker
    case 'conquest':
      return evt.attacker
    case 'elimination':
      return evt.eliminator
    case 'team-eliminated':
      return evt.lastPlayer
    default:
      return (evt as { player: string }).player
  }
}

/**
 * Group raw GameEvent[] into EventRow[] for display.
 *
 * Grouping strategy:
 *   - Events within BUNDLE_THRESHOLD_MS of each other that form a logical
 *     bundle (attack + conquest + elimination(s) + post-conquest-transfer)
 *     are folded into one row with the attack line as primary and subsequent
 *     events as sub-lines.
 *   - phase-advance events are silently dropped.
 *   - Output is in reverse chronological order (newest first).
 */
export function groupEvents(
  events: GameEvent[],
  resolve: PlayerResolver,
  territoryName: (id: string) => string,
): EventRow[] {
  const rows: EventRow[] = []
  let i = 0

  while (i < events.length) {
    const evt = events[i]!
    const primaryLine = formatEventLine(evt, resolve, territoryName)

    // Silently skip suppressed event kinds (phase-advance).
    if (primaryLine === null) {
      i++
      continue
    }

    // Greedily consume bundleable events that share this timestamp.
    const primaryTs = evt.ts
    const sub: string[] = []
    // The primary kind is always the first entry. Bundled sub-event
    // kinds are appended so the kind-filter sees them and matches
    // rows like "attack with conquered=true" when the user picks
    // "conquest" in the filter chip strip.
    const kinds: GameEvent['kind'][] = [evt.kind]
    // Seed the related-players set with the primary actor + any
    // counterpart on the primary event (e.g. defender on an attack).
    // Bundled events extend it below.
    const relatedSet = new Set<string>()
    relatedSet.add(eventPlayerId(evt))
    // Empty-string defender = neutral territory; skip the add so the
    // by-player filter doesn't gain a phantom "" target.
    if (evt.kind === 'attack' && evt.defender !== '') relatedSet.add(evt.defender)
    let j = i + 1

    while (j < events.length) {
      const next = events[j]!

      // Stop if outside the bundle window.
      if (Math.abs(next.ts - primaryTs) > BUNDLE_THRESHOLD_MS) break

      // phase-advance inside a bundle: silently drop.
      if (next.kind === 'phase-advance') {
        j++
        continue
      }

      // conquest / elimination / post-conquest-transfer are sub-lines
      // when they share a timestamp with an attack. Fog of war: a hidden
      // attack (a hidden row that isn't a follow-up) bundles the same way,
      // and a hidden conquest or move (`followUp`) folds in like the real
      // one, so every viewer's log has the same rows as the actor's.
      const opensBundle = evt.kind === 'attack' || (evt.kind === 'hidden' && !evt.followUp)
      const isBundleable =
        opensBundle &&
        (next.kind === 'conquest' ||
          next.kind === 'elimination' ||
          next.kind === 'team-eliminated' ||
          next.kind === 'post-conquest-transfer' ||
          (next.kind === 'hidden' && !!next.followUp))

      if (isBundleable) {
        // A hidden follow-up adds nothing to say under the attack.
        const subLine = next.kind === 'hidden' ? null : formatEventLine(next, resolve, territoryName)
        if (subLine !== null) sub.push(subLine)
        kinds.push(next.kind)
        relatedSet.add(eventPlayerId(next))
        if (next.kind === 'elimination') relatedSet.add(next.eliminated)
        j++
      } else {
        break
      }
    }

    // Derive turn number: turn-start events carry turnNumber; others don't
    // have a turn field directly, so we track it via the nearest turn-start.
    const turn = evt.kind === 'turn-start' ? evt.turnNumber : 0

    // Derive the "defender" identity for the second colour dot. Only
    // populated on two-sided rows (combat); single-actor rows leave it
    // undefined so renderers skip the second chip.
    let defenderId: string | undefined
    if (evt.kind === 'attack') defenderId = evt.defender
    else if (evt.kind === 'conquest') defenderId = evt.defender
    else if (evt.kind === 'elimination') defenderId = evt.eliminated

    rows.push({
      key: `${evt.ts}-${evt.kind}-${i}`,
      primary: primaryLine,
      sub,
      kinds,
      turn,
      ts: evt.ts,
      isTurnStart: evt.kind === 'turn-start',
      isGameEnd: evt.kind === 'game-end',
      playerId: eventPlayerId(evt),
      defenderId,
      // Drop falsy entries: player-less events (e.g. `game-end`) make
      // eventPlayerId return undefined, and '' is the neutral non-player.
      // Either would become a `key={undefined}`/empty filter chip in the log.
      relatedPlayerIds: [...relatedSet].filter(Boolean),
      startEventIndex: i,
      // Last applied event index in this row's bundle. j is the index
      // of the next NON-bundled event, so j-1 is the last one folded in.
      eventIndex: j - 1,
    })

    i = j
  }

  // Reverse to newest-first.
  return rows.reverse()
}
